import { useMutation } from '@tanstack/react-query';
import {
    Geometry,
    MetricsClient,
    MetricUploadType,
} from '~/backend/metrics-client';
import { MetricsTypeEnum } from '~/backend/types';

/**
 * A React hook to upload DICOM files using the provided client and sessionID to metrics API.
 *
 * @param {BackendClient} client - The client used to interact with the backend services.
 * @param {MetricsTypeEnum} metricsType - Which model to use for the metrics.
 * @param {Object} callbacks - An object containing callback functions for success and error handling.
 * - @param {Function} callbacks.onSuccess - Callback function to execute upon successful upload.
 * - @param {Function} callbacks.onError - Callback function to execute upon an error in the upload process.
 *
 * @returns {Object} An object containing:
 * - @returns {Error | null} error - Contains an error if the upload failed, or null otherwise.
 * - @returns {Function} submit - A function to initiate the upload of DICOM files. Accepts `action`, `dicomBlobs`, and `count` as parameters.
 */
export const useMetricsUpload = (
    client: MetricsClient,
    metricsType: MetricsTypeEnum,
    {
        onSuccess,
        onError,
    }: {
        onSuccess: () => void;
        onError: () => void;
    }
) => {
    const mutation = useMutation({
        mutationFn: async ({
            dicomBlobs,
            groundTruthSOPInstanceUID,
            userFileSOPInstanceUID,
            geometry,
        }: {
            dicomBlobs: MetricUploadType[];
            groundTruthSOPInstanceUID: string;
            userFileSOPInstanceUID: string;
            geometry: Geometry;
        }) => {
            const fileSubmitted = await client.submitMetrics(
                metricsType,
                dicomBlobs,
                groundTruthSOPInstanceUID,
                userFileSOPInstanceUID,
                geometry
            );
            return fileSubmitted;
        },
        onSuccess,
        onError,
    });

    const submit = (
        dicomBlobs: MetricUploadType[],
        groundTruthSOPInstanceUID: string,
        userFileSOPInstanceUID: string,
        geometry: Geometry
    ) => {
        mutation.mutate({
            dicomBlobs,
            groundTruthSOPInstanceUID,
            userFileSOPInstanceUID,
            geometry,
        });
    };

    return {
        error: mutation.error,
        submit,
    };
};
